import { Link } from "react-router-dom";
import { useCart, useAuth } from "@/App";
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

const CartPage = () => {
  const { cart, updateCartItem, removeFromCart } = useCart();
  const { token } = useAuth();

  const formatPrice = (price) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
    }).format(price);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center" data-testid="cart-page-login-required">
        <div className="text-center">
          <div className="w-24 h-24 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-6">
            <ShoppingBag className="w-12 h-12 text-stone-300" />
          </div>
          <h1 className="text-2xl font-bold text-[#1D3557] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
            Connectez-vous pour voir votre panier
          </h1>
          <Button asChild className="bg-[#C65D47] hover:bg-[#A84A38] rounded-full">
            <Link to="/login">Se connecter</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="cart-page">
      <div className="container-custom py-12">
        <div className="flex items-center gap-4 mb-8">
          <Link to="/boutique" className="flex items-center gap-2 text-stone-500 hover:text-[#C65D47]">
            <ArrowLeft className="w-5 h-5" />
            Continuer mes achats
          </Link>
        </div>

        <h1 className="text-3xl md:text-4xl font-bold text-[#1D3557] mb-12" style={{ fontFamily: 'Fraunces, serif' }}>
          Mon Panier
        </h1>

        {cart.items?.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-cart">
            <div className="w-24 h-24 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-6">
              <ShoppingBag className="w-12 h-12 text-stone-300" />
            </div>
            <h2 className="text-2xl font-bold text-[#1D3557] mb-4">Votre panier est vide</h2>
            <p className="text-stone-600 mb-8">Découvrez notre sélection de produits personnalisables</p>
            <Button asChild className="bg-[#C65D47] hover:bg-[#A84A38] rounded-full px-8">
              <Link to="/boutique">Voir la boutique</Link>
            </Button>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-6">
              {cart.items.map((item) => (
                <div
                  key={item.id}
                  className="flex gap-6 p-6 bg-white rounded-2xl shadow-sm"
                  data-testid={`cart-item-${item.id}`}
                >
                  <div className="w-32 h-32 rounded-xl overflow-hidden flex-shrink-0 bg-stone-100">
                    <img
                      src={item.product_image}
                      alt={item.product_name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-[#1D3557] text-lg">{item.product_name}</h3>
                        {item.customization && (
                          <p className="text-sm text-stone-500 mt-1">
                            Personnalisation: {item.customization}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="p-2 text-stone-400 hover:text-[#E63946] transition-colors"
                        data-testid={`remove-${item.id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>

                    <div className="flex items-center justify-between mt-4">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => updateCartItem(item.id, item.quantity - 1)}
                          className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center hover:bg-stone-200 transition-colors"
                          data-testid={`decrease-${item.id}`}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-12 text-center font-medium text-lg">{item.quantity}</span>
                        <button
                          onClick={() => updateCartItem(item.id, item.quantity + 1)}
                          className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center hover:bg-stone-200 transition-colors"
                          data-testid={`increase-${item.id}`}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-stone-500">{formatPrice(item.price)} / unité</p>
                        <p className="text-xl font-bold text-[#C65D47]">
                          {formatPrice(item.price * item.quantity)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div>
              <div className="bg-white rounded-2xl shadow-sm p-8 sticky top-28">
                <h2 className="text-xl font-bold text-[#1D3557] mb-6" style={{ fontFamily: 'Fraunces, serif' }}>
                  Récapitulatif
                </h2>

                <div className="space-y-4 pb-6 border-b border-stone-200">
                  <div className="flex justify-between text-stone-600">
                    <span>Sous-total</span>
                    <span>{formatPrice(cart.total)}</span>
                  </div>
                  <div className="flex justify-between text-stone-600">
                    <span>Livraison</span>
                    <span className="text-sm">Calculée à l'étape suivante</span>
                  </div>
                </div>

                <div className="flex justify-between items-center py-6">
                  <span className="text-lg font-semibold text-[#1D3557]">Total</span>
                  <span className="text-2xl font-bold text-[#C65D47]" data-testid="cart-total">
                    {formatPrice(cart.total)}
                  </span>
                </div>

                <Button
                  asChild
                  className="w-full bg-[#C65D47] hover:bg-[#A84A38] rounded-full h-14 text-lg font-semibold"
                >
                  <Link to="/checkout" data-testid="proceed-to-checkout">
                    Passer commande
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </Button>

                <p className="text-xs text-stone-500 text-center mt-4">
                  Paiement sécurisé • Livraison en France et Corse
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartPage;
